import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MangaCard from '@/components/base/MangaCard';
import { mangaList, categories } from '@/mocks/manga';

const categoryMap: Record<string, { name: string; description: string; keywords: string }> = {
  hanman: {
    name: '韩漫',
    description: '亲亲漫画韩漫专区，精选韩国热门漫画免费全集下拉式在线阅读。高攀、极道鲜师等热门韩漫持续更新中。',
    keywords: '亲亲漫画韩漫,韩漫免费,韩漫在线阅读,韩国漫画,韩漫推荐,韩漫全集,下拉式韩漫',
  },
  riman: {
    name: '日漫',
    description: '亲亲漫画日漫专区，日本经典热血漫画免费全集下拉式在线阅读。咒术回战、鬼灭之刃、一拳超人等人气日漫持续更新。',
    keywords: '亲亲漫画日漫,日漫免费,日漫在线阅读,日本漫画,日漫推荐,日漫全集,下拉式日漫',
  },
  guoman: {
    name: '国漫',
    description: '亲亲漫画国漫专区，国风原创优质漫画免费全集下拉式在线阅读。仙逆、凡人修仙传、都市至尊狂婿等国漫精品推荐。',
    keywords: '亲亲漫画国漫,国漫免费,国漫在线阅读,国产漫画,国漫推荐,国漫全集,下拉式国漫',
  },
  remen: {
    name: '热门推荐',
    description: '亲亲漫画热门推荐，全网最受欢迎漫画免费全集下拉式在线阅读。海量韩漫日漫国漫热门推荐，好看的漫画大全不间断更新。',
    keywords: '亲亲漫画热门,热门漫画推荐,免费漫画,漫画大全,热门漫画在线阅读,漫画推荐',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [mounted, setMounted] = useState(false);

  const catInfo = categoryMap[slug || ''] || categoryMap['remen'];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMounted(true);
  }, [slug, searchQuery]);

  const filteredManga = useMemo(() => {
    let result = mangaList;
    if (slug === 'hanman') {
      result = mangaList.filter((m) => m.category === '韩漫');
    } else if (slug === 'riman') {
      result = mangaList.filter((m) => m.category === '日漫');
    } else if (slug === 'guoman') {
      result = mangaList.filter((m) => m.category === '国漫');
    } else if (slug === 'remen' || !slug) {
      result = [...mangaList].sort((a, b) => b.views - a.views);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.author.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [slug, searchQuery]);

  const pageTitle = searchQuery
    ? `搜索"${searchQuery}" - ${catInfo.name} | 亲亲漫画`
    : `${catInfo.name} | 亲亲漫画 免费全集下拉式在线漫画大全`;

  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": pageTitle,
    "description": catInfo.description,
    "url": `https://qinqinmanga.com/category/${slug || 'remen'}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "亲亲漫画",
      "url": "https://qinqinmanga.com/"
    }
  };

  const currentCategory = categories.find(c => c.slug === slug);

  return (
    <>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionPageSchema) }}
    />
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catInfo.name },
            ]}
          />

          <div className="py-6 md:py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                  {currentCategory && (
                    <span className="w-9 h-9 inline-flex items-center justify-center bg-primary-100 text-primary-500 rounded-lg mr-2 align-middle">
                      <i className={`${currentCategory.icon} text-lg`}></i>
                    </span>
                  )}
                  {searchQuery ? `搜索: "${searchQuery}"` : catInfo.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">{catInfo.description}</p>
              </div>
            </div>

            <div className="mb-6 flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    window.REACT_APP_NAVIGATE(`/category/${cat.slug}`);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    slug === cat.slug || (!slug && cat.slug === 'remen')
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredManga.length === 0 ? (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                  <i className="ri-search-line text-3xl"></i>
                </span>
                <p className="text-foreground-500 text-sm">没有找到相关漫画，试试其他关键词吧</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
}